import { Document, DocumentsApi, InvestorsApi } from 'api';
import useApiCall from 'hooks/use-api-call';
import React, { FunctionComponent, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalFooter, ModalContent, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';
import { FileWithDocType } from 'src/ui/atoms/file-upload';

export interface EditTaxInformationFields {
  id: string;
  taxIdentificationNumber: string;
  nonAssessmentCertificate: boolean;
  nonAssessmentCertificateDocId: string;
  nonAssessmentCertificateDoc: FileWithDocType;
  isSubjectToGermanTax: boolean;
}
export interface EditTaxInformationModalProps {
  onHideEditTaxInformation: () => void;
  fetchInvestorDetails: () => void;
  className?: string;
  values: {
    id: string;
    isSubjectToGermanTax?: boolean;
    taxIdentificationNumber: string;
    nonAssessmentCertificate: boolean;
    nonAssessmentCertificateDoc?: Document;
  };
}

const EditTaxInformationForm = makeModalForm<EditTaxInformationFields>();

const EditTaxInformationModal: FunctionComponent<EditTaxInformationModalProps> = (props) => {
  const { onHideEditTaxInformation, fetchInvestorDetails, values } = props;

  const [uploadedDocument, setUploadedDocument] = useState<Document | undefined | null>(
    values?.nonAssessmentCertificateDoc || null,
  );

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);

  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const editTaxInformation = (fieldsValues: StateValues<EditTaxInformationFields>) => {
    withApi(async () => {
      const patchBody = {
        id: values.id,
        patchedInvestorUpdateRequest: {
          taxInformation: {
            ...fieldsValues,
            nonAssessmentCertificate: fieldsValues.nonAssessmentCertificate,
            isSubjectToGermanTax: !fieldsValues.isSubjectToGermanTax,
          },
        },
      };

      if (fieldsValues.nonAssessmentCertificate && fieldsValues.nonAssessmentCertificateDoc?.file) {
        const nonAssessmentCertificateDocResponse = await documentsApi.documentsCreate({
          file: fieldsValues.nonAssessmentCertificateDoc.file,
        });
        patchBody.patchedInvestorUpdateRequest.taxInformation.nonAssessmentCertificateDocId =
          nonAssessmentCertificateDocResponse.id;
      }

      await investorsApi.investorsPartialUpdate(patchBody);

      fetchInvestorDetails();
      onHideEditTaxInformation();
    });
  };

  return (
    <>
      <Modal onClose={onHideEditTaxInformation}>
        <EditTaxInformationForm
          initial={{
            taxIdentificationNumber: values.taxIdentificationNumber,
            isSubjectToGermanTax: values.isSubjectToGermanTax === undefined ? undefined : !values.isSubjectToGermanTax,
            nonAssessmentCertificate: values.nonAssessmentCertificate,
          }}
          onSubmit={(values: StateValues<EditTaxInformationFields>) => {
            return editTaxInformation(values);
          }}
          i18nKey="taxInformationForm"
        >
          <EditTaxInformationForm.ValueProvider>
            {(values) => {
              return (
                <>
                  <ModalHeader>
                    <Translate name="dashboardIssuerInvestorDetails.editTaxInformationForm.title" />
                  </ModalHeader>
                  <ModalContent>
                    <strong>
                      <Translate as="p" name="dashboardIssuerInvestorDetails.editTaxInformationForm.subtitleTaxId" />
                    </strong>
                    <EditTaxInformationForm.Group name="taxIdentificationNumber">
                      <EditTaxInformationForm.Input placeholder="-" />
                    </EditTaxInformationForm.Group>
                    <EditTaxInformationForm.Group name="isSubjectToGermanTax" label={null}>
                      <EditTaxInformationForm.Checkbox>
                        <Translate name="taxInformationForm.fields.isSubjectToGermanTax.label" />
                      </EditTaxInformationForm.Checkbox>
                    </EditTaxInformationForm.Group>
                    <strong>
                      <Translate
                        as="p"
                        name="dashboardIssuerInvestorDetails.editTaxInformationForm.subtitleNonAssessmentCertificate"
                      />
                    </strong>
                    <EditTaxInformationForm.Group name="nonAssessmentCertificate" label={null}>
                      <EditTaxInformationForm.Checkbox>
                        <Translate name="taxInformationForm.fields.hasNonAssessmentCertificate.label" />
                      </EditTaxInformationForm.Checkbox>
                    </EditTaxInformationForm.Group>

                    {!!values.nonAssessmentCertificate && (
                      <>
                        {uploadedDocument && (
                          <FileUploadDocumentContainer
                            uploadedFile={{
                              file: {
                                name: uploadedDocument.name,
                              } as File,
                            }}
                            onRemoveDocument={() => setUploadedDocument(null)}
                          />
                        )}
                        {!uploadedDocument && (
                          <EditTaxInformationForm.Group
                            name="nonAssessmentCertificateDoc"
                            required={values.nonAssessmentCertificate}
                          >
                            <EditTaxInformationForm.FileUpload />
                            {EditTaxInformationForm.Validators.FileSize(5000000)}
                          </EditTaxInformationForm.Group>
                        )}
                      </>
                    )}
                  </ModalContent>
                  <ModalFooter>
                    <ModalFooterButtons
                      actionButtons={[
                        {
                          name: 'cancel',
                          content: <Translate name="common.cancel" />,
                          size: 'large',
                          onClick: onHideEditTaxInformation,
                        },
                        {
                          name: 'update',
                          content: <Translate name="common.save" />,
                          variant: 'primary',
                          type: 'submit',
                          size: 'large',
                        },
                      ]}
                    />
                  </ModalFooter>
                </>
              );
            }}
          </EditTaxInformationForm.ValueProvider>
        </EditTaxInformationForm>
      </Modal>
    </>
  );
};

export default EditTaxInformationModal;
